import React, { lazy, Suspense, useEffect } from "react";
import { Navigate, Outlet, Route, Routes } from "react-router-dom";
import theme from "./assets/theme";

// App CSS
import "./App.css";

// Font CSS
import "./assets/css/nucleo-svg.css";
import "./assets/css/nucleo-icons.css";

// Material import
import { Box, CircularProgress, ThemeProvider } from "@mui/material";
import CssBaseline from "@mui/material/CssBaseline";

// Routing
import { routesFor, homeFor } from "./routing/routing";
import { isHelpDesk } from "utils/role";
import appStore from "store/general";
import ConfirmModal from "components/Modals/ConfirmModal";
import userStore from "store/userStore";
import authRoutes from "routing/authRouting";
import { TOKEN_KEYS } from "utils/constant";
import { Toaster } from "sonner";

// Components

// layout
const AppRoute = lazy(() => import("./layouts/AppLayout"));

function App() {
	const { token, verifyUser, fetchingUser }: any = userStore();
	const { confirmModal }: any = appStore();
	const helpDesk = isHelpDesk(token?.[TOKEN_KEYS.ACCESS_TOKEN]);

	const getRoutes = (allRoutes) =>
		allRoutes.map((route) => {
			if (route.collapse) {
				return getRoutes(route.collapse);
			}

			if (route.route) {
				return (
					<Route path={route.route} element={route.component} key={route.key} />
				);
			}
			return null;
		});

	useEffect(() => {
		if (localStorage.getItem(TOKEN_KEYS.REFRESH_TOKEN)) verifyUser();
	}, [verifyUser]);

	return (
		<ThemeProvider theme={theme}>
			<CssBaseline />
			<Suspense>
				{/* Show the loader only while verifying AND we have no access
				    token to render with. Once a token is present (rehydrated or
				    freshly fetched) keep the routes mounted — otherwise the
				    background verifyUser() refresh would blank the screen mid-
				    session. */}
				{fetchingUser && !token?.[TOKEN_KEYS.ACCESS_TOKEN] ? (
					<Box className="flex justify-center items-center h-screen">
						<CircularProgress />
					</Box>
				) : (
					<Routes>
						{token?.[TOKEN_KEYS.ACCESS_TOKEN] ? (
							<Route element={<AppRoute />}>
								{getRoutes(routesFor(helpDesk))}
								<Route path="*" element={<Navigate to={homeFor(helpDesk)} />} />
							</Route>
						) : (
							<Route element={<Outlet />}>
								{getRoutes(authRoutes)}
								<Route path="*" element={<Navigate to="/login" />} />
							</Route>
						)}
						<Route path="/" element={<Navigate to="/login" />} />
					</Routes>
				)}
				{confirmModal && (
					<ConfirmModal
						visible
						handleCancel={() => confirmModal.handleCancel?.()}
						handleOk={() => confirmModal.handleOk?.()}
						title={confirmModal.title || "Modal Title"}
						message={confirmModal.message}
						primaryBtnText={confirmModal.primaryBtnText}
						secondaryBtnText={confirmModal.secondaryBtnText}
						className={confirmModal.className}
					/>
				)}
			</Suspense>
			<Toaster
				position="top-center"
				richColors
				duration={3000}
				toastOptions={{ style: { padding: "10px" } }}
			/>
		</ThemeProvider>
	);
}

export default App;
